/**
 * The one channel between the game loop and React. The engine emits; the HUD listens.
 *
 * Keep it this way. Reading React state inside the loop (60x a second) or re-rendering React every
 * frame is the single biggest cause of a stuttering web game.
 *
 * A tiny emitter rather than an engine's own — the UI should not care which engine is running.
 */

export type GameStatus = 'playing' | 'paused' | 'over';

export const GameEvent = {
	/** payload: number — current score */
	Score: 'score',
	/** payload: GameStatus */
	Status: 'status',
	/** payload: number — bonus awarded for threading a hazard closely */
	NearMiss: 'nearMiss',
} as const;

type Handler = (payload: never) => void;

class Emitter {
	private handlers = new Map<string, Set<Handler>>();

	on<T>(event: string, handler: (payload: T) => void): void {
		if (!this.handlers.has(event)) this.handlers.set(event, new Set());
		this.handlers.get(event)!.add(handler as Handler);
	}

	off<T>(event: string, handler: (payload: T) => void): void {
		this.handlers.get(event)?.delete(handler as Handler);
	}

	emit<T>(event: string, payload: T): void {
		const set = this.handlers.get(event);
		if (!set) return;
		for (const handler of set) (handler as (p: T) => void)(payload);
	}
}

export const gameBus = new Emitter();
